import React, { useState } from 'react';
import { Button } from './ui/button';
import { Play, X, ArrowRight, Video, Star, Zap, Sparkles } from 'lucide-react';
import { mockData } from './mock';
import { useScrollAnimation, createScrollAnimationClasses } from '../hooks/useScrollAnimation';

export const VideoSection = () => {
  const [currentVideo, setCurrentVideo] = useState(null);
  const [headerRef, headerVisible] = useScrollAnimation({ delay: 0 });
  const [video1Ref, video1Visible] = useScrollAnimation({ delay: 200 });
  const [video2Ref, video2Visible] = useScrollAnimation({ delay: 400 });
  const [footerRef, footerVisible] = useScrollAnimation({ delay: 600 });

  const openVideo = (videoData) => {
    setCurrentVideo(videoData);
  };

  const closeVideo = () => {
    setCurrentVideo(null);
  };

  return (
    <section id="video" className="py-24 bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 relative overflow-hidden">
      {/* Enhanced Background Decorations */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-green-500/5 rounded-full blur-3xl"></div>
        
        {/* Enhanced Floating Particles */}
        <div className="absolute inset-0 opacity-20">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-blue-400 rounded-full animate-ping"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 2}s`
              }}
            />
          ))}
        </div>
        
        {/* Enhanced Grid Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23595959' fill-opacity='0.1'%3E%3Cpolygon points='50 0 60 40 100 50 60 60 50 100 40 60 0 50 40 40'/%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Enhanced Header */}
        <div ref={headerRef} className={`text-center mb-20 ${createScrollAnimationClasses('fadeInUp', headerVisible)}`}>
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500/10 to-purple-500/10 px-6 py-3 rounded-full mb-6 border border-blue-400/20 backdrop-blur-sm">
            <Video size={16} className="text-blue-600" />
            <span className="text-blue-700 text-sm font-semibold">Video Showcase</span>
          </div>
          <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Experience Our Innovation
            <span 
              className="block bg-clip-text text-transparent"
              style={{ 
                backgroundImage: 'linear-gradient(to right, rgb(6 47 136), rgb(147 51 234))' 
              }}
            >
              in Action
            </span>
          </h2>
          <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
            Watch how our cutting-edge technology is transforming cities and 
            creating smarter urban environments across Oman. See the future 
            of intelligent infrastructure in motion.
          </p>
        </div>

        {/* Enhanced Video Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Technology Demo Video */}
          <div ref={video1Ref} className={`group cursor-pointer ${createScrollAnimationClasses('fadeInLeft', video1Visible)}`} onClick={() => openVideo(mockData.video)}>
            <div className="relative overflow-hidden rounded-3xl shadow-2xl bg-black transform group-hover:scale-105 transition-all duration-500 hover:shadow-3xl">
              <img 
                src={mockData.video.thumbnail}
                alt="Technology Demo"
                className="w-full h-72 lg:h-80 object-cover group-hover:scale-110 transition-transform duration-700"
              />
              
              {/* Enhanced Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent group-hover:from-black/80 transition-all duration-500"></div>
              
              {/* Floating Elements */}
              <div className="absolute top-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="flex items-center gap-2 bg-blue-500/90 backdrop-blur-sm rounded-lg px-3 py-2">
                  <Zap size={14} className="text-white" />
                  <span className="text-white text-sm font-semibold">Tech Demo</span>
                </div>
              </div>
              
              {/* Enhanced Play Button */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-24 h-24 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:scale-125 group-hover:bg-blue-500 group-hover:text-white transition-all duration-500 shadow-2xl">
                  <Play size={36} className="ml-1" />
                </div>
              </div>

              {/* Enhanced Content */}
              <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                  <span className="text-blue-400 text-sm font-semibold">Technology Showcase</span>
                </div>
                <h3 className="text-2xl lg:text-3xl font-bold text-white mb-3 group-hover:text-blue-200 transition-colors leading-tight">
                  {mockData.video.title}
                </h3>
                <p className="text-gray-300 mb-4 leading-relaxed">
                  {mockData.video.description}
                </p>
                <div className="flex items-center text-blue-400 font-semibold group-hover:text-blue-300 transition-colors">
                  <span>Watch Demo</span>
                  <ArrowRight size={18} className="ml-2 group-hover:translate-x-2 transition-transform duration-300" />
                </div>
              </div>

              {/* Enhanced Duration Badge */}
              <div className="absolute top-4 right-4 bg-black/80 backdrop-blur-sm text-white px-4 py-2 rounded-xl text-sm font-semibold border border-white/20">
                3:45
              </div>
            </div>
          </div>

          {/* Company Overview Video */}
          <div ref={video2Ref} className={`group cursor-pointer ${createScrollAnimationClasses('fadeInRight', video2Visible)}`} onClick={() => openVideo(mockData.companyVideo)}>
            <div className="relative overflow-hidden rounded-3xl shadow-2xl bg-black transform group-hover:scale-105 transition-all duration-500 hover:shadow-3xl">
              <img 
                src={mockData.companyVideo.thumbnail}
                alt="Company Overview"
                className="w-full h-72 lg:h-80 object-cover group-hover:scale-110 transition-transform duration-700"
              />
              
              {/* Enhanced Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent group-hover:from-black/80 transition-all duration-500"></div>
              
              {/* Floating Elements */}
              <div className="absolute top-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="flex items-center gap-2 bg-green-500/90 backdrop-blur-sm rounded-lg px-3 py-2">
                  <Star size={14} className="text-white" />
                  <span className="text-white text-sm font-semibold">Company Story</span>
                </div>
              </div>
              
              {/* Enhanced Play Button */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-24 h-24 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:scale-125 group-hover:bg-green-500 group-hover:text-white transition-all duration-500 shadow-2xl">
                  <Play size={36} className="ml-1" />
                </div>
              </div>

              {/* Enhanced Content */}
              <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span className="text-green-400 text-sm font-semibold">Company Overview</span>
                </div>
                <h3 className="text-2xl lg:text-3xl font-bold text-white mb-3 group-hover:text-green-200 transition-colors leading-tight">
                  {mockData.companyVideo.title}
                </h3>
                <p className="text-gray-300 mb-4 leading-relaxed">
                  {mockData.companyVideo.description}
                </p>
                <div className="flex items-center text-green-400 font-semibold group-hover:text-green-300 transition-colors">
                  <span>Learn More</span>
                  <ArrowRight size={18} className="ml-2 group-hover:translate-x-2 transition-transform duration-300" />
                </div>
              </div>

              {/* Enhanced Duration Badge */}
              <div className="absolute top-4 right-4 bg-black/80 backdrop-blur-sm text-white px-4 py-2 rounded-xl text-sm font-semibold border border-white/20">
                2:30
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Footer */}
        <div ref={footerRef} className={`mt-16 text-center ${createScrollAnimationClasses('fadeInUp', footerVisible)}`}>
          <div className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-sm rounded-full px-6 py-3 border border-gray-200">
            <Sparkles size={16} className="text-blue-600" />
            <span className="text-gray-700 text-sm font-semibold">Click on any video to watch our comprehensive showcase</span>
          </div>
        </div>
      </div>

      {/* Enhanced Video Modal */}
      {currentVideo && (
        <div className="fixed inset-0 bg-black/95 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="relative w-full max-w-6xl">
            {/* Enhanced Close Button */}
            <Button
              onClick={closeVideo}
              variant="ghost"
              size="lg"
              className="absolute -top-16 right-0 text-white hover:text-gray-300 z-10 hover:bg-white/10 rounded-full w-12 h-12"
            >
              <X size={24} />
            </Button>

            {/* Enhanced Video Player */}
            <div className="relative aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl border border-white/20">
              <video 
                src={currentVideo.url}
                controls
                autoPlay
                className="w-full h-full"
                onError={(e) => {
                  console.error('Video failed to load:', e);
                  alert('Video temporarily unavailable. Please try again later.');
                }}
              >
                Your browser does not support the video tag.
              </video>
              
              {/* Video Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none"></div>
            </div>
            
            {/* Video Info */}
            <div className="mt-6 text-center">
              <h3 className="text-2xl font-bold text-white mb-2">{currentVideo.title}</h3>
              <p className="text-gray-300">{currentVideo.description}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};